/*
 * Legal document parser.
 *
 * The legal copy is the source of truth and lives untouched in `content/*.ts`
 * as an exported raw string (this app is Create React App, which has no Vite
 * `?raw` import). This module turns that raw text into a structured,
 * section-anchored model the reader shell renders. Keeping the words as the
 * approved string (never retyped into TSX) is what guarantees the published
 * pages match the reviewed legal text character for character: a lawyer edits
 * the string, the page follows.
 *
 * The grammar is intentionally the small subset these documents use: one H1
 * title, a Vigência/Versão pair, an intro, `##` sections, paragraphs, ordered
 * and unordered lists, one pipe table, `>` callouts and `---` rules, with
 * inline bold, `code`, bare URLs and e-mails. It is not a general markdown
 * engine and does not try to be.
 */

export interface InlineText {
  t: "text";
  value: string;
}
export interface InlineStrong {
  t: "strong";
  children: Inline[];
}
export interface InlineCode {
  t: "code";
  value: string;
}
export interface InlineLink {
  t: "link";
  href: string;
  internal: boolean;
  children: Inline[];
}
export type Inline = InlineText | InlineStrong | InlineCode | InlineLink;

export interface ParagraphBlock {
  t: "p";
  content: Inline[];
}
export interface ListBlock {
  t: "ul" | "ol";
  items: Inline[][];
}
export interface TableBlock {
  t: "table";
  head: Inline[][];
  rows: Inline[][][];
}
export interface NoteBlock {
  t: "note";
  blocks: Block[];
}
export interface RuleBlock {
  t: "hr";
}
export type Block = ParagraphBlock | ListBlock | TableBlock | NoteBlock | RuleBlock;

export interface LegalSection {
  id: string;
  number: string | null;
  title: string;
  blocks: Block[];
}

export interface LegalDoc {
  title: string;
  vigencia: string | null;
  versao: string | null;
  notice: Block[] | null;
  intro: Block[];
  sections: LegalSection[];
}

const HEADING_RE = /^#{1,6}\s/;
const SECTION_RE = /^##\s/;
const QUOTE_RE = /^>\s?/;
const RULE_RE = /^-{3,}\s*$/;
const TABLE_RE = /^\s*\|/;
const UL_RE = /^\s*-\s+/;
const OL_RE = /^\s*\d+\.\s+/;

// URLs first, then e-mails. Trailing sentence punctuation is trimmed back out
// of a URL match so "…em https://dakasa.me/privacidade, que…" links only the
// address, not the comma.
const LINK_RE = /(https?:\/\/[^\s)]+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

function slug(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function linkify(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) out.push({ t: "text", value: text.slice(last, m.index) });
    if (m[1]) {
      let url = m[1];
      let trailing = "";
      const tm = url.match(/[.,;:!?]+$/);
      if (tm) {
        trailing = tm[0];
        url = url.slice(0, -trailing.length);
      }
      const internal = url.match(/^https?:\/\/dakasa\.me(\/[^\s]*)?$/i);
      if (internal) {
        const path = internal[1] && internal[1] !== "/" ? internal[1] : "/";
        out.push({ t: "link", href: path, internal: true, children: [{ t: "text", value: url }] });
      } else {
        out.push({ t: "link", href: url, internal: false, children: [{ t: "text", value: url }] });
      }
      if (trailing) out.push({ t: "text", value: trailing });
      last = m.index + m[1].length;
    } else {
      const email = m[2];
      out.push({
        t: "link",
        href: `mailto:${email}`,
        internal: false,
        children: [{ t: "text", value: email }],
      });
      last = m.index + email.length;
    }
  }
  if (last < text.length) out.push({ t: "text", value: text.slice(last) });
  return out;
}

function parseInlineNoBold(text: string): Inline[] {
  const out: Inline[] = [];
  const codeRe = /`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = codeRe.exec(text)) !== null) {
    if (m.index > last) out.push(...linkify(text.slice(last, m.index)));
    out.push({ t: "code", value: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...linkify(text.slice(last)));
  return out;
}

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  const boldRe = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = boldRe.exec(text)) !== null) {
    if (m.index > last) out.push(...parseInlineNoBold(text.slice(last, m.index)));
    out.push({ t: "strong", children: parseInlineNoBold(m[1]) });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...parseInlineNoBold(text.slice(last)));
  return out;
}

function parseTable(rows: string[]): TableBlock {
  const cells = rows.map((r) =>
    r
      .trim()
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim()),
  );
  const head = (cells[0] ?? []).map(parseInline);
  // cells[1] is the `|---|---|` separator row; the body starts at index 2.
  const body = cells.slice(2).map((row) => row.map(parseInline));
  return { t: "table", head, rows: body };
}

function trimRules(blocks: Block[]): Block[] {
  let start = 0;
  let end = blocks.length;
  while (start < end && blocks[start].t === "hr") start++;
  while (end > start && blocks[end - 1].t === "hr") end--;
  return blocks.slice(start, end);
}

function parseBlocks(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      i++;
      continue;
    }
    if (QUOTE_RE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && /^>/.test(lines[i])) {
        inner.push(lines[i].replace(QUOTE_RE, ""));
        i++;
      }
      blocks.push({ t: "note", blocks: trimRules(parseBlocks(inner)) });
      continue;
    }
    if (RULE_RE.test(line.trim())) {
      blocks.push({ t: "hr" });
      i++;
      continue;
    }
    if (TABLE_RE.test(line)) {
      const tableLines: string[] = [];
      while (i < lines.length && TABLE_RE.test(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      blocks.push(parseTable(tableLines));
      continue;
    }
    if (UL_RE.test(line)) {
      const items: Inline[][] = [];
      while (i < lines.length && UL_RE.test(lines[i])) {
        items.push(parseInline(lines[i].replace(UL_RE, "")));
        i++;
      }
      blocks.push({ t: "ul", items });
      continue;
    }
    if (OL_RE.test(line)) {
      const items: Inline[][] = [];
      while (i < lines.length && OL_RE.test(lines[i])) {
        items.push(parseInline(lines[i].replace(OL_RE, "")));
        i++;
      }
      blocks.push({ t: "ol", items });
      continue;
    }
    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !QUOTE_RE.test(lines[i]) &&
      !RULE_RE.test(lines[i].trim()) &&
      !TABLE_RE.test(lines[i]) &&
      !UL_RE.test(lines[i]) &&
      !OL_RE.test(lines[i]) &&
      !HEADING_RE.test(lines[i])
    ) {
      paragraph.push(lines[i]);
      i++;
    }
    if (paragraph.length > 0) {
      blocks.push({ t: "p", content: parseInline(paragraph.join(" ")) });
    } else {
      i++;
    }
  }
  return blocks;
}

export function parseLegalDoc(raw: string): LegalDoc {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  let i = 0;

  while (i < lines.length && lines[i].trim() === "") i++;

  let notice: Block[] | null = null;
  if (i < lines.length && QUOTE_RE.test(lines[i])) {
    const inner: string[] = [];
    while (i < lines.length && /^>/.test(lines[i])) {
      inner.push(lines[i].replace(QUOTE_RE, ""));
      i++;
    }
    notice = trimRules(parseBlocks(inner));
  }

  let title = "";
  while (i < lines.length) {
    if (/^#\s/.test(lines[i])) {
      title = lines[i].replace(/^#\s+/, "").trim();
      i++;
      break;
    }
    i++;
  }

  const introLines: string[] = [];
  let vigencia: string | null = null;
  let versao: string | null = null;
  while (i < lines.length && !SECTION_RE.test(lines[i])) {
    const line = lines[i];
    const vg = line.match(/^\*\*Vigência[^:]*:\*\*\s*(.+)$/);
    const vs = line.match(/^\*\*Versão:\*\*\s*(.+)$/);
    if (vg) {
      vigencia = vg[1].trim();
    } else if (vs) {
      versao = vs[1].trim();
    } else {
      introLines.push(line);
    }
    i++;
  }
  const intro = trimRules(parseBlocks(introLines));

  const sections: LegalSection[] = [];
  while (i < lines.length) {
    if (!SECTION_RE.test(lines[i])) {
      i++;
      continue;
    }
    const heading = lines[i].replace(/^##\s+/, "").trim();
    i++;
    const body: string[] = [];
    while (i < lines.length && !SECTION_RE.test(lines[i])) {
      body.push(lines[i]);
      i++;
    }
    const numbered = heading.match(/^(\d+)\.\s+(.*)$/);
    const number = numbered ? numbered[1] : null;
    const sectionTitle = numbered ? numbered[2] : heading;
    const id = number ? `sec-${number}` : `sec-${slug(heading)}`;
    sections.push({ id, number, title: sectionTitle, blocks: trimRules(parseBlocks(body)) });
  }

  return { title, vigencia, versao, notice, intro, sections };
}
